<template>
  <MetaComponent />

  <BlackFridayBanner
    @start="onSpecialBannerStart"
    @finish="onSpecialBannerFinish"
  />
  <DefaultBanner v-if="!root.meta.userSignedIn && showDefaultBanner.value" />
  <WebinarBannerCustom v-if="root.meta.userSignedIn" />
  <SpringBanner
    v-if="!root.meta.userSignedIn"
    @start="onSpecialBannerStart"
    @finish="onSpecialBannerFinish"
  />

  <div :class="$style.outer">
    <div :class="$style.leftSide" />
    <div :class="$style.ctn">
      <Header />
      <div>
        <Ancor name="General" />
        <SectionComponent :class="$style.section" title="General Info">
          <General />
        </SectionComponent>

        <Ancor name="Behavior" />
        <SectionComponent :class="$style.section" title="Behavior activities">
          <BehaviorActivities />
        </SectionComponent>

        <Ancor name="MalConf" />
        <SectionComponent :class="$style.section" title="Malware configuration">
          <MalwareConfiguration />
        </SectionComponent>

        <Ancor name="Static information" />
        <SectionComponent :class="$style.section" title="Static information">
          <StaticInformation />
        </SectionComponent>

        <!--googleoff: all-->
        <!--noindex-->
        <Ancor name="Video" />
        <SectionComponent :class="$style.section" title="Video and screenshots">
          <VideoAndScreenshots />
        </SectionComponent>
        <!--googleon: all-->
        <!--/noindex-->

        <Ancor name="Process" parent="System events" />
        <SectionComponent
          :class="[$style.section, $style.processes]"
          title="Processes"
        >
          <Processes />
        </SectionComponent>

        <template v-if="root.registryActivity">
          <Ancor name="Registry activity" parent="System events" />
          <SectionComponent :class="$style.section" title="Registry activity">
            <RegistryActivity />
          </SectionComponent>
        </template>

        <Ancor name="Files activity" parent="System events" />
        <SectionComponent :class="$style.section" title="Files activity">
          <FilesActivity />
        </SectionComponent>

        <Ancor name="Network" />
        <SectionComponent :class="$style.section" title="Network activity">
          <NetworkActivity />
        </SectionComponent>

        <Ancor name="Debug" parent="System events" />
        <SectionComponent :class="$style.section" title="Debug output strings">
          <DebugOutputStrings />
        </SectionComponent>
      </div>
      <Footer />
    </div>
    <div :class="$style.rightSide">
      <GoTopButton />
    </div>
  </div>

  <div v-if="printRef.value" :class="$style.printLoader">
    <SvgIcon width="24" height="24" src="icon-sprite.svg#print_button" />
    <div>Loading...</div>
  </div>
</template>

<script>
import {
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  watch,
} from 'vue';

import '@fontsource-variable/montserrat/index.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/teko/700.css';

import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/montserrat/900.css';

import Ancor from './components/Ancor.vue';
import BehaviorActivities from './components/BehaviorActivities.vue';
import DebugOutputStrings from './components/DebugOutputStrings.vue';
import FilesActivity from './components/FilesActivity.vue';
import Footer from './components/Footer.vue';
import General from './components/General.vue';
import Header from './components/Header.vue';
import NetworkActivity from './components/NetworkActivity.vue';
import Processes from './components/Processes.vue';
import RegistryActivity from './components/RegistryActivity.vue';
import RPCRequests from './components/RPCRequests.vue';
import SectionComponent from './components/SectionComponent.vue';
import StaticInformation from './components/StaticInformation.vue';
import MalwareConfiguration from './components/MalwareConfiguration.vue';
import Synchronization from './components/Synchronization.vue';
import VideoAndScreenshots from './components/VideoAndScreenshots.vue';
import MetaComponent from './components/MetaComponent.vue';
import GoTopButton from './components/GoTopButton.vue';
import DefaultBanner from './components/banners/DefaultBanner.vue';
import SpringSaleBanner from './components/banners/SpringSaleBanner.vue';
import BlackFridayBanner from './components/banners/BlackFridayBanner.vue';
import SvgIcon from './components/SvgIcon.vue';
import WebinarBannerCustom from './components/banners/WebinarBannerCustom.vue';
import SpringBanner from './components/banners/SpringBanner.vue';

export default defineComponent({
  name: 'App',
  components: {
    MetaComponent,
    Header,
    SectionComponent,
    General,
    BehaviorActivities,
    StaticInformation,
    WebinarBannerCustom,
    MalwareConfiguration,
    VideoAndScreenshots,
    Processes,
    RegistryActivity,
    FilesActivity,
    Synchronization,
    RPCRequests,
    NetworkActivity,
    DebugOutputStrings,
    Footer,
    Ancor,
    GoTopButton,
    DefaultBanner,
    SpringSaleBanner,
    SpringBanner,
    BlackFridayBanner,
    SvgIcon,
  },
  props: {
    root: { type: Object, required: true },
  },
  setup(props) {
    provide('root', props.root);

    const printRef = reactive({ value: null });

    provide('printRef', printRef);

    /**
     * change to 'true' if remove special banner like 'BlackFridayBanner'
     * default 'false' - fix micro lag - changed default banner to special
     */
    const showDefaultBanner = reactive({ value: false });

    const beforePrint = () => {
      printRef.value = 'windowPrintEvent';
    };

    const afterPrint = () => {
      printRef.value = null;
    };

    const onSpecialBannerStart = () => {
      showDefaultBanner.value = false;
    };
    const onSpecialBannerFinish = () => {
      showDefaultBanner.value = true;
    };

    const initCookieConsent = async () => {
      const { run } = await import('./components/cookieConsent/index.js');
      run();
    };

    const initSearchParamsManager = async () => {
      const { searchParamSaversManager } = await import(
        './shared/utils/searchParams/index.js'
      );
      searchParamSaversManager.init();
    };

    onMounted(async () => {
      window.addEventListener('beforeprint', beforePrint);
      window.addEventListener('afterprint', afterPrint);

      await initCookieConsent();
      await initSearchParamsManager();
    });

    onUnmounted(() => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    });

    watch(printRef, newValueRef => {
      if (!newValueRef.value || newValueRef.value === 'windowPrintEvent') {
        return;
      }

      /* safari long load fix (reason: google tag manager scripts and media fetch) */
      const printWhenLoaded = () => {
        if (document.readyState !== 'complete') {
          window.addEventListener('load', printWhenLoaded);
          return;
        }
        window.removeEventListener('load', printWhenLoaded);

        try {
          if (!document.execCommand('print', false, null)) {
            window.print();
          }
        } catch {
          window.print();
        }
      };

      nextTick(printWhenLoaded);
    });

    return {
      printRef,
      showDefaultBanner,
      onSpecialBannerStart,
      onSpecialBannerFinish,
    };
  },
});
</script>
<style lang="scss" module>
.processes {
  page-break-before: always;
}
.section + * {
  margin-top: 50px;
}
.ctn {
  background: #ffffff;
  box-shadow: 0px 5px 20px 5px #1e2634;
  width: 1140px;
  padding: 30px 30px 20px;
  position: relative;
  flex: none;

  @media screen and (max-width: 1255px) {
    width: 100%;
  }

  @media screen and (max-width: 620px) {
    padding: 10px 10px 20px;
  }

  @media print {
    box-shadow: none;
    padding: 0;
  }
}
.outer {
  display: flex;
  padding: 40px 20px;
  justify-content: center;
  min-width: 1240px;

  @media screen and (max-width: 1255px) {
    min-width: auto;
  }

  @media print {
    padding: 0;
  }
}

.leftSide {
  width: 20px + 40px;

  @media screen and (max-width: 1255px) {
    display: none;
  }
}

.rightSide {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  padding-left: 20px;
  padding-top: 100vh;

  @media screen and (max-width: 1255px) {
    position: fixed;
    top: 0;
    right: 20px;
    bottom: 0;
    padding: 0;
    width: 0;
    z-index: 100;

    a {
      background: #ccc;
      border-radius: 50%;
    }
  }
}

@keyframes pulse {
  0% {
    scale: 1.4;
  }

  50% {
    scale: 1.35;
  }

  100% {
    scale: 1.3;
  }
}

.printLoader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  --fill: #fff;
  color: #fff;
  text-align: center;
  gap: 10px;

  svg {
    animation: 1s pulse infinite;
    animation-direction: reverse;
  }

  @media print {
    display: none;
  }
}
</style>

<style lang="scss">
button {
  cursor: pointer;
  border-radius: 0;
  background: initial;
  border: none;
  display: block;
}
a,
h2 {
  text-decoration: none;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
}
svg {
  display: block;
}
table {
  border-collapse: collapse;
}
* {
  text-size-adjust: none;
  page-break-inside: auto;
  border: none;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  line-height: 120%;
  font-display: block;
  font-family: Roboto;
}
ul {
  list-style-type: none;
}
img {
  border: none;
  outline: none;
}
body,
html {
  scroll-behavior: unset;
  background: #323e55;
  @media print {
    background: transparent;
  }
}

b {
  font-weight: 500;
}
table {
  width: 100%;
}
td,
th {
  vertical-align: top;
}

:root {
  --info-color: #0c86c7;
  --warning-color: #f58014;
  --danger-color: #e35653;
  --secondary-color: #00a29e;
  --neutral-color: #6c757d;

  --secondary-cell-color: rgba(239, 243, 246, 0.5);
  --table-border-color: #d4e3f0;
  --table-head-color: #eaeff3;
  --table-head-color-hover: #e0eaf2;

  --table-head-border-color: var(--table-head-color);
  --key-color: rgba(63, 71, 87, 0.85);
  --accordeon-border-color: #e0eaf2;
  --accordeon-border-color-hover: #c9e0f2;
  --activities-accent-color: #e35653;
}

@media print {
  :root {
    --logo-icon-color-text: #575757;
    --logo-icon-color-sign: #636363;
    --logo-icon-color-illustration: #8a8a8a;
    --secondary-cell-color: #f5f5f5;
    --table-border-color: #dfdfdf;
    --table-head-color: #efefef;
    --table-head-border-color: var(--table-border-color);
    --accordeon-border-color: #dfdfdf;
    --activities-accent-color: #989898;
  }

  html {
    zoom: 90%;
  }

  .viewer-container {
    display: none;
  }
}

* {
  -webkit-print-color-adjust: exact !important; /* Chrome, Safari 6 – 15.3, Edge */
  color-adjust: exact !important; /* Firefox 48 – 96 */
  print-color-adjust: exact !important; /* Firefox 97+, Safari 15.4+ */
}
</style>
